import React, { useState } from "react";
import {
  Button,
  Group,
  Text,
  TextInput,
  Textarea,
  Title,
  ColorInput,
  useMantineColorScheme,
  Container,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useNavigate } from "react-router-dom";
import { IconArrowLeft } from "@tabler/icons-react";
import { useCreateRoleMutation } from "generated/graphql";

/**
 * RoleCreate
 *
 * Admin page to create a new role.
 * Roles are created with basic info; actions can be assigned in the edit page.
 */
export default function RoleCreate() {
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const [createRole, { loading: creating }] = useCreateRoleMutation();

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("");
  const [iconUrl, setIconUrl] = useState("");

  const isValid = name.trim() !== "";

  async function handleCreate() {
    if (!isValid) return;

    try {
      const result = await createRole({
        variables: {
          input: {
            name: name.trim(),
            description: description.trim() || undefined,
            color: color || undefined,
            iconUrl: iconUrl.trim() || undefined,
          },
        },
      });

      if (result.data?.createRole?.success && result.data.createRole.role) {
        notifications.show({
          title: "Success",
          message: `Role "${result.data.createRole.role.name}" created successfully`,
          color: "green",
        });
        navigate(`/admin-hub/roles/${result.data.createRole.role.id}`);
      } else {
        notifications.show({
          title: "Error",
          message: result.data?.createRole?.errorMessage || "Failed to create role",
          color: "red",
        });
      }
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err?.message || "An error occurred while creating the role",
        color: "red",
      });
    }
  }

  return (
    <Container size="xl" px="lg" py="lg">
      <Group justify="flex-start" mb="sm">
        <Button
          variant="filled"
          color="blue"
          className="bg-blue-600 hover:bg-blue-700 text-white"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate("/admin-hub/roles")}
          size="sm"
        >
          Back to Roles
        </Button>
      </Group>
      <div className="space-y-4 max-w-2xl">
        <Title c={isDark ? "white" : "black"}>Create Role</Title>
        <Text c={isDark ? "gray.4" : "gray.7"}>
          Create a new role. After creation, you can assign permissions (actions) to the role.
        </Text>

        <div className="space-y-4">
          <TextInput
            label="Role Name"
            placeholder="e.g., Moderator, Team Captain"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            withAsterisk
            autoComplete="off"
          />

          <Textarea
            label="Description"
            placeholder="Optional description of what this role is for..."
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            minRows={2}
          />

          <ColorInput
            label="Color"
            placeholder="Pick a color for this role"
            description="Used for displaying role badges"
            value={color}
            onChange={setColor}
            format="hex"
            swatches={[
              "#DC2626",
              "#F59E0B",
              "#10B981",
              "#3B82F6",
              "#8B5CF6",
              "#EC4899",
              "#6B7280",
            ]}
          />

          <TextInput
            label="Icon URL"
            placeholder="https://example.com/icon.png"
            description="Optional URL to an icon for this role"
            value={iconUrl}
            onChange={(e) => setIconUrl(e.currentTarget.value)}
            autoComplete="off"
          />

          <Group mt="xl">
            <Button
              className="bg-blue-500 hover:bg-blue-600"
              onClick={handleCreate}
              disabled={!isValid || creating}
              loading={creating}
            >
              Create Role
            </Button>
            <Button variant="light" color="gray" onClick={() => navigate(-1)}>
              Cancel
            </Button>
          </Group>
        </div>
      </div>
    </Container>
  );
}
